import { useReducer } from 'react';
import { useSelector } from 'react-redux';

import { getAPIUrl, getBaseAPIUrl } from '../../api/apiUtils';
import axiosInstance from '../../api/axiosConfig';
import Log from '../../api/logs/logs';
import HttpRequestReducer from './reducer/HttpRequestReducer';

const useHttpRequest = () => {
  const [state, dispatch] = useReducer(HttpRequestReducer, {
    status: 'idle',
    data: null,
    error: null,
  });
  // eslint-disable-next-line no-shadow
  const { accountId, authenticityToken } = useSelector((state) => state.internalReducer.internal);
  const baseUrl = accountId && getAPIUrl(accountId);

  const baseApiUrl = getBaseAPIUrl();

  const noBaseUrl = () => !baseUrl;

  const responseOutput = (responseData) => {
    if (responseData.error) {
      dispatch({ type: 'ERROR', error: responseData.error });
    } else {
      dispatch({ type: 'RESOLVED', data: responseData });
    }
  };

  const request = async ({ url, data, requestType }) => {
    if (noBaseUrl()) return;
    dispatch({ type: 'STARTED' });
    try {
      const responseData = await axiosInstance.request({
        method: requestType,
        url: baseUrl + url,
        data: {
          authenticity_token: authenticityToken,
          ...data,
        },
      });
      responseOutput(responseData.data);
    } catch (error) {
      dispatch({ type: 'ERROR', error: error });
    }
  };

  const fetchRequest = async ({ url, params }) => {
    if (noBaseUrl()) return;
    dispatch({ type: 'STARTED' });
    try {
      const responseData = await axiosInstance.request({
        method: 'get',
        url: baseUrl + url,
        params: params,
      });
      responseOutput(responseData.data);
    } catch (error) {
      dispatch({ type: 'ERROR', error: error });
    }
  };

  const blobDownloadRequest = async ({ url, params }) => {
    if (noBaseUrl()) return;
    dispatch({ type: 'STARTED' });
    try {
      const responseData = await axiosInstance.request({
        method: 'get',
        url: baseUrl + url,
        responseType: 'blob',
        params: params,
      });

      const downloadUrl = URL.createObjectURL(new Blob([responseData.data], { type: 'application/octet-stream' }));
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = responseData.headers.filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      responseOutput(responseData.data);
    } catch (error) {
      dispatch({ type: 'ERROR', error: error });
    }
  };

  const heartbeatRequest = async (logType = 'user_active') => {
    if (noBaseUrl()) return;
    dispatch({ type: 'STARTED' });
    const heartbeatConfig = Log.heartbeat(logType);
    try {
      const responseData = await axiosInstance.request({
        method: 'post',
        url: baseApiUrl + heartbeatConfig.url,
        data: {
          authenticity_token: authenticityToken,
          ...heartbeatConfig.params,
        },
      });
      responseOutput(responseData.data);
    } catch (error) {
      dispatch({ type: 'ERROR', error: error });
    }
  };

  return { state, request, fetchRequest, blobDownloadRequest, heartbeatRequest };
};

export default useHttpRequest;
